import * as R from 'remeda'
import { useCallback, useRef, useState } from 'react'
import { PayloadIn } from '../xmpp-client/types'
import { getNewRoomAssignees, RoomAssignees } from '../xmpp-client/getNewRoomAssignees'
import { Affiliations, Marker, ParsedMarker, ParsedPayloadIn, RoomEventsMap } from './types'
import { useImmer } from './useImmer'

type Event = PayloadIn | Marker | Affiliations

export const useChatDataState = () => {
  const [{ events }, setState] = useImmer<{
    events: RoomEventsMap
  }>({
    events: {},
  })
  const markedEvents = useRef<{
    [roomId: string]: {
      lastMarkedEvent?: {
        id: string
        timestamp: string
      }
      markedEventsIds: Set<string>
    }
  }>({})
  const [roomAssignees, setRoomAssignees] = useState<RoomAssignees>({})

  const setEvent = useCallback(
    (event: Event, callback?: (event: ParsedPayloadIn | ParsedMarker) => void) => {
      setState(draft => {
        if (event.type === 'Affiliations' || !event.id) {
          return draft
        }
        const roomEvents = draft.events[event.roomId] ?? {}
        const oldEvent = roomEvents[event.id]
        const mergedEvent = {
          ...event,
          id: event.id,
          timestamp: event.timestamp ?? oldEvent?.timestamp ?? new Date().toISOString(),
        }
        roomEvents[mergedEvent.id] = mergedEvent
        draft.events[event.roomId] = roomEvents
        callback?.(mergedEvent)
        return draft
      })
    },
    [setState]
  )

  const registerEvent = useCallback(
    (event: Event, callback?: (event: ParsedPayloadIn | ParsedMarker) => void) => {
      if (event.type === 'Affiliations') {
        setRoomAssignees(prevRoomAssignees =>
          getNewRoomAssignees({
            prevRoomAssignees,
            affiliationEvent: event,
          })
        )
      } else if (event.type === 'Marker') {
        const roomData = markedEvents.current[event.roomId] ?? {
          markedEventsIds: new Set<string>(),
        }
        roomData.markedEventsIds.add(event.eventId)
        markedEvents.current[event.roomId] = roomData
      }
      setEvent(event, callback)
    },
    [setEvent]
  )

  const resetAffiliations = useCallback(
    (
      roomId: string,
      newAffiliationUsers: Array<{ id: string; affiliation: Affiliations['affiliation'] }>
    ) => {
      setRoomAssignees(prevRoomAssignees => {
        return {
          ...prevRoomAssignees,
          [roomId]: {
            version: undefined,
            timestamp: undefined,
            assignees: R.mapToObj(newAffiliationUsers, user => [user.id, user.affiliation]),
          },
        }
      })
    },
    []
  )

  const isMarked = useCallback(
    (roomId: string, event: { readonly id?: string; readonly timestamp?: string }) => {
      if (!event.id) {
        return false
      }
      const roomData = markedEvents.current[roomId]
      if (!roomData) {
        return false
      }
      if (roomData.lastMarkedEvent?.id === event.id) {
        return true
      }
      if (roomData.markedEventsIds.has(event.id)) {
        if (!event.timestamp) {
          return true
        }
        if (!roomData.lastMarkedEvent || roomData.lastMarkedEvent.timestamp < event.timestamp) {
          roomData.lastMarkedEvent = {
            id: event.id,
            timestamp: event.timestamp,
          }
        }
        if (roomData.lastMarkedEvent && event.timestamp <= roomData.lastMarkedEvent.timestamp) {
          // we can remove older markers, only the newest one is interesting,
          // anything before that is read by definition
          roomData.markedEventsIds.delete(event.id)
        }
        return true
      }
      return false
    },
    []
  )

  return {
    events,
    isMarked,
    roomAssignees,
    registerEvent,
    resetAffiliations,
  }
}
